import { Zap, ChevronLeft, ChevronRight, Check, AlertTriangle } from 'lucide-react';

const DAYS = ['Mon 7', 'Tue 8', 'Wed 9', 'Thu 10', 'Fri 11', 'Sat 12', 'Sun 13'];

const EMPLOYEES = [
  { name: 'Sarah M.', color: 'hsl(210, 60%, 82%)', accent: 'hsl(210, 55%, 45%)' },
  { name: 'James R.', color: 'hsl(145, 55%, 80%)', accent: 'hsl(145, 50%, 38%)' },
  { name: 'Isabelle K.', color: 'hsl(30, 65%, 82%)', accent: 'hsl(30, 60%, 42%)' },
  { name: 'Tommy L.', color: 'hsl(280, 50%, 84%)', accent: 'hsl(280, 45%, 48%)' },
  { name: 'Laura P.', color: 'hsl(350, 60%, 84%)', accent: 'hsl(350, 50%, 48%)' },
  { name: 'Jeremy D.', color: 'hsl(55, 60%, 82%)', accent: 'hsl(55, 55%, 35%)' },
];

interface Shift {
  employee: number;
  time: string;
  hours: string;
  confirmed?: boolean;
}

const SCHEDULE: Record<number, Shift[]> = {
  0: [
    { employee: 0, time: '09:00–17:00', hours: '7h paid (1h break)', confirmed: true },
    { employee: 1, time: '09:00–17:00', hours: '7h paid (1h break)', confirmed: true },
    { employee: 2, time: '12:00–18:00', hours: '5.5h paid (30m break)' },
  ],
  1: [
    { employee: 0, time: '09:00–17:00', hours: '7h paid (1h break)', confirmed: true },
    { employee: 3, time: '09:00–17:00', hours: '7h paid (1h break)' },
    { employee: 4, time: '12:00–20:00', hours: '7h paid (1h break)' },
  ],
  2: [
    { employee: 1, time: '09:00–17:00', hours: '7h paid (1h break)', confirmed: true },
    { employee: 2, time: '09:00–15:00', hours: '5.5h paid (30m break)', confirmed: true },
    { employee: 5, time: '12:00–20:00', hours: '7h paid (1h break)' },
  ],
  3: [
    { employee: 0, time: '09:00–17:00', hours: '7h paid (1h break)', confirmed: true },
    { employee: 3, time: '09:00–17:00', hours: '7h paid (1h break)' },
    { employee: 5, time: '12:00–18:00', hours: '5.5h paid (30m break)' },
  ],
  4: [
    { employee: 1, time: '09:00–17:00', hours: '7h paid (1h break)', confirmed: true },
    { employee: 4, time: '09:00–17:00', hours: '7h paid (1h break)' },
    { employee: 2, time: '12:00–20:00', hours: '7h paid (1h break)' },
  ],
  5: [
    { employee: 0, time: '10:00–18:00', hours: '7h paid (1h break)' },
    { employee: 3, time: '10:00–16:00', hours: '5.5h paid (30m break)' },
  ],
  6: [],
};

const STAFFING: Record<number, { required: number; actual: number }> = {
  0: { required: 3, actual: 3 },
  1: { required: 3, actual: 3 },
  2: { required: 3, actual: 3 },
  3: { required: 3, actual: 3 },
  4: { required: 3, actual: 3 },
  5: { required: 2, actual: 2 },
  6: { required: 0, actual: 0 },
};

export default function ScheduleMockup() {
  return (
    <div className="select-none pointer-events-none" aria-hidden="true">
      {/* Main content */}
      <div className="bg-background">
          {/* Top bar */}
          <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 border-b">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">Schedule</span>
              <span className="text-xs text-muted-foreground hidden sm:inline">›</span>
              <span className="text-xs text-muted-foreground hidden sm:inline">Main Store</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground shadow-sm">
                <Zap className="h-3 w-3" />
                <span className="hidden sm:inline">Auto-Generate</span>
                <span className="sm:hidden">Auto</span>
              </button>
            </div>
          </div>

          {/* Week nav */}
          <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b bg-muted/30">
            <button className="p-1 rounded hover:bg-muted">
              <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <span className="text-xs font-medium text-foreground">Apr 7 – Apr 13, 2026</span>
            <button className="p-1 rounded hover:bg-muted">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </div>

          {/* Employee legend */}
          <div className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 border-b bg-muted/20 overflow-x-auto">
            {EMPLOYEES.map((emp) => (
              <span
                key={emp.name}
                className="flex items-center gap-1 text-[10px] text-muted-foreground whitespace-nowrap"
              >
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: emp.accent }}
                />
                <span className="hidden sm:inline">{emp.name}</span>
              </span>
            ))}
          </div>

          {/* Schedule grid */}
          <div className="overflow-x-auto">
            <div className="grid min-w-[580px]" style={{ gridTemplateColumns: `repeat(7, minmax(0, 1fr))` }}>
              {/* Day headers */}
              {DAYS.map((day, i) => {
                const staffing = STAFFING[i];
                const isSunday = i === 6;
                return (
                  <div
                    key={day}
                    className={`px-1.5 sm:px-2 py-1.5 border-b border-r text-center ${isSunday ? 'bg-muted/40' : ''}`}
                  >
                    <div className="text-[10px] sm:text-xs font-semibold text-foreground">{day}</div>
                    {staffing.required > 0 ? (
                      <div className="flex items-center justify-center gap-0.5 mt-0.5">
                        <Check className="h-2.5 w-2.5 text-emerald-500" />
                        <span className="text-[9px] text-emerald-600 font-medium">
                          {staffing.actual}/{staffing.required}
                        </span>
                      </div>
                    ) : (
                      <div className="text-[9px] text-muted-foreground/50 mt-0.5">Closed</div>
                    )}
                  </div>
                );
              })}

              {/* Shift cells */}
              {DAYS.map((day, dayIndex) => {
                const shifts = SCHEDULE[dayIndex];
                const isSunday = dayIndex === 6;
                return (
                  <div
                    key={`cell-${day}`}
                    className={`border-r border-b p-1 min-h-[80px] sm:min-h-[110px] ${isSunday ? 'bg-muted/20' : ''}`}
                  >
                    {shifts.length === 0 && isSunday && (
                      <div className="flex items-center justify-center h-full text-[9px] text-muted-foreground/40">
                        No shifts
                      </div>
                    )}
                    <div className="flex flex-col gap-0.5 sm:gap-1">
                      {shifts.map((shift, si) => {
                        const emp = EMPLOYEES[shift.employee];
                        return (
                          <div
                            key={si}
                            className="rounded px-1 sm:px-1.5 py-0.5 sm:py-1 border transition-shadow"
                            style={{
                              backgroundColor: emp.color,
                              borderColor: `${emp.accent}40`,
                              borderLeftWidth: '2px',
                              borderLeftColor: emp.accent,
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-[9px] sm:text-[10px] font-semibold truncate" style={{ color: emp.accent }}>
                                {emp.name}
                              </span>
                              {shift.confirmed && (
                                <Check className="h-2.5 w-2.5 shrink-0" style={{ color: emp.accent }} />
                              )}
                            </div>
                            <div className="text-[8px] sm:text-[9px] text-muted-foreground/80 truncate">{shift.time}</div>
                            <div className="text-[7px] sm:text-[8px] text-muted-foreground/60 truncate hidden sm:block">{shift.hours}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom summary bar */}
          <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-t bg-muted/20">
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-muted-foreground">
                <span className="font-semibold text-foreground">6</span> employees
              </span>
              <span className="text-[10px] text-muted-foreground">
                <span className="font-semibold text-foreground">17</span> shifts
              </span>
              <span className="text-[10px] text-muted-foreground">
                <span className="font-semibold text-foreground">109.5h</span> total
              </span>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
              <Check className="h-3 w-3" />
              <span className="hidden sm:inline">All staffing met</span>
            </div>
          </div>
      </div>
    </div>
  );
}
